import React, { useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../auth/AuthContext";
import Select from "../components/Select";

const API = process.env.REACT_APP_API_URL;
const STORAGE_KEY = "trade_adjustment_page_v1";
const TASKS_KEY = "trade_adjustment_tasks_v1";

function readStored() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}
function writeStored(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}
function readStoredTasks() {
  try {
    return JSON.parse(localStorage.getItem(TASKS_KEY) || "null") || [];
  } catch {
    return [];
  }
}
function writeStoredTasks(tasks) {
  try {
    if (tasks.length > 0) {
      localStorage.setItem(TASKS_KEY, JSON.stringify(tasks));
    } else {
      localStorage.removeItem(TASKS_KEY);
    }
  } catch { /* ignore */ }
}

function fmtDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-CA");
}

const COLUMNS = [
  { key: "id",           label: "ID" },
  { key: "scenathon_id", label: "Scenathon ID" },
  { key: "country",      label: "Country" },
  { key: "date",         label: "Date" },
];

function SortIcon({ direction }) {
  if (!direction) return <span className="ml-1 text-slate-300">↕</span>;
  return (
    <span className="ml-1 text-slate-600">
      {direction === "asc" ? "↑" : "↓"}
    </span>
  );
}

export default function TradeAdjustmentPage() {
  const { accessToken, csrfToken, logout, selectedScenathon } = useAuth();
  const stored = useMemo(() => readStored(), []);

  const [loadingScenathons, setLoadingScenathons] = useState(true);
  const [scenathons, setScenathons] = useState([]);

  const [scenathonId, setScenathonId] = useState(
    selectedScenathon?.id || stored?.scenathonId || "",
  );
  const [pathwayId, setPathwayId] = useState(stored?.pathwayId || "");

  const [loadingPathways, setLoadingPathways] = useState(false);
  const [pathways, setPathways] = useState([]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [enqueuing, setEnqueuing] = useState(false);
  const [runningFairshare, setRunningFairshare] = useState(false);

  // task progress tracking
  const [tasksInProgress, setTasksInProgress] = useState(() => readStoredTasks());
  const [taskStatuses, setTaskStatuses] = useState({});
  const hadTasksRef = useRef(false);

  // if tasks were persisted from a previous visit, mark hadTasksRef so completion fires
  useEffect(() => {
    if (tasksInProgress.length > 0) {
      hadTasksRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // table state
  const [sortKey, setSortKey] = useState("country");
  const [sortDir, setSortDir] = useState("asc");

  const apiFetch = async (url, opts = {}) => {
    const headers = {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
      ...(opts.headers || {}),
    };
    const res = await fetch(url, { credentials: "include", ...opts, headers });
    if (res.status === 401) { logout?.(); throw new Error("Session expired"); }
    return res;
  };

  // persist selections
  useEffect(() => {
    writeStored({ scenathonId, pathwayId });
  }, [scenathonId, pathwayId]);

  // load scenathons
  useEffect(() => {
    let alive = true;
    setLoadingScenathons(true);
    apiFetch(`${API}/api/scenathon/scenathons`)
      .then((r) => r.json())
      .then((d) => { if (alive) setScenathons(Array.isArray(d?.scenathons) ? d.scenathons : []); })
      .catch((e) => console.error(e))
      .finally(() => { if (alive) setLoadingScenathons(false); });
    return () => { alive = false; };
  }, []);

  // load pathways when scenathon changes
  useEffect(() => {
    if (!scenathonId) { setPathways([]); setPathwayId(""); return; }
    setLoadingPathways(true);
    let alive = true;

    const isAllowedPathway = (pathway) => {
      if (!pathway || typeof pathway !== "object") return false;
      const raw = String(pathway.label || pathway.value || pathway.pathway_name || "");
      return !raw.toLowerCase().includes("- adjusted");
    };

    apiFetch(`${API}/api/scenathon/available-pathways/${scenathonId}`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        const nextPathways = Array.isArray(d.pathways) ? d.pathways : [];
        const filteredPathways = nextPathways.filter(isAllowedPathway);
        setPathways(filteredPathways);
        setPathwayId((prev) => (
          filteredPathways.some((pathway) => String(pathway.value) === String(prev)) ? prev : ""
        ));
      })
      .catch((e) => { console.error(e); if (alive) setPathways([]); })
      .finally(() => { if (alive) setLoadingPathways(false); });
    return () => { alive = false; };
  }, [scenathonId]);

  useEffect(() => {
    if (selectedScenathon?.id && selectedScenathon.id !== scenathonId) {
      setScenathonId(String(selectedScenathon.id));
    }
  }, [selectedScenathon?.id, scenathonId]);

  // reset table on selection change
  useEffect(() => { setRows([]); setError(""); }, [scenathonId, pathwayId]);

  const loadTradeData = async ({ signal } = {}) => {
    if (!scenathonId || !pathwayId) return;

    setLoading(true);
    setRows([]);
    setError("");

    const params = new URLSearchParams({ scenathonId: String(scenathonId), pathway: String(pathwayId) });

    try {
      const res = await apiFetch(`${API}/api/tradeadjustment/trade-adjustment?${params}`, {
        signal,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.error || `HTTP ${res.status}`);
      setRows(d.rows || []);
    } catch (e) {
      if (e.name === "AbortError") return;
      console.error(e);
      setError(e.message || "Could not load data.");
    } finally {
      setLoading(false);
    }
  };

  // fetch table data when both selected
  useEffect(() => {
    if (!scenathonId || !pathwayId) return;
    let alive = true;
    const controller = new AbortController();

    loadTradeData({ signal: controller.signal }).then(() => {
      if (!alive) return;
    });
    return () => { alive = false; };
  }, [scenathonId, pathwayId]);

  // persist tasks across navigation
  useEffect(() => {
    writeStoredTasks(tasksInProgress);
  }, [tasksInProgress]);

  // poll for task status updates
  useEffect(() => {
    if (tasksInProgress.length === 0) return;
    hadTasksRef.current = true;

    const pollInterval = setInterval(async () => {
      try {
        const res = await apiFetch(`${API}/api/tradeadjustment/check-tasks`, {
          method: "POST",
          body: JSON.stringify({ taskIds: tasksInProgress }),
        });

        const data = await res.json();
        if (res.ok && data.statuses) {
          setTaskStatuses(data.statuses);

          const stillInProgress = tasksInProgress.filter(
            (id) => data.statuses[id] && data.statuses[id] !== "done",
          );
          setTasksInProgress(stillInProgress);
        }
      } catch (e) {
        console.error("Error polling task status:", e);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [tasksInProgress]);

  // when all tasks complete, reload table and notify
  useEffect(() => {
    if (tasksInProgress.length !== 0 || !hadTasksRef.current) return;
    hadTasksRef.current = false;

    if (!scenathonId || !pathwayId) return;

    const params = new URLSearchParams({ scenathonId: String(scenathonId), pathway: String(pathwayId) });
    apiFetch(`${API}/api/tradeadjustment/trade-adjustment?${params}`)
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d?.error || `HTTP ${r.status}`);
        setRows(d.rows || []);
      })
      .catch((e) => console.error("Error reloading after tasks:", e));

    Swal.fire({
      icon: "success",
      title: "Tasks completed",
      text: "All trade adjustment tasks finished successfully.",
      confirmButtonColor: "#3085d6",
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksInProgress.length]);

  const scenathonOptions = useMemo(
    () => scenathons.map((s) => ({ value: String(s.id), label: s.name || `Scenathon ${s.id}` })),
    [scenathons],
  );

  const filtered = useMemo(() => {
    return [...rows].sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      if (typeof av === "number" && typeof bv === "number") {
        return sortDir === "asc" ? av - bv : bv - av;
      }
      return sortDir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [rows, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleRefreshTable = () => {
    if (!scenathonId || !pathwayId || loading) return;
    loadTradeData();
  };

  const getPathwayName = () => {
    const found = pathways.find((p) => String(p.value) === String(pathwayId));
    return found?.label || pathwayId;
  };

  const handleSubmitAllTasks = async () => {
    if (filtered.length === 0 || enqueuing) return;
    const confirmation = await Swal.fire({
      icon: "warning",
      title: "Execute Trade Adjustment?",
      text: `This will execute the trade adjustment process for ${getPathwayName()}.`,
      showCancelButton: true,
      confirmButtonText: "Yes, execute",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#16a34a",
      cancelButtonColor: "#64748b",
      reverseButtons: true,
    });

    if (!confirmation.isConfirmed) return;

    setEnqueuing(true);
    const pathwayName = getPathwayName();
    try {
      // Execute trade-adjustment in database (only)
      const execRes = await apiFetch(`${API}/api/tradeadjustment/execute-trade-adjustment`, {
        method: "POST",
        body: JSON.stringify({ scenathonId, pathwayName }),
      });
      if (!execRes.ok) {
        const err = await execRes.json().catch(() => null);
        throw new Error(err?.error || `HTTP ${execRes.status}`);
      }
      Swal.fire({
        icon: "success",
        title: "Trade Adjustment Executed",
        text: "The trade adjustment process has been executed successfully.",
      });
    } catch (e) {
      console.error(e);
      Swal.fire({ icon: "error", title: "Error", text: e.message || "Failed to execute trade adjustment" });
    } finally {
      setEnqueuing(false);
    }
  };

  const handleRunFairshare = async () => {
    if (!scenathonId) return;
    setRunningFairshare(true);
    try {
      const res = await apiFetch(`${API}/api/fair/run`, {
        method: "POST",
        body: JSON.stringify({
          scenathonId: 3,
          pathwayId: 1,
          pathwayName: "CT",
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      Swal.fire({
        icon: "success",
        title: "Fairshare executed",
        text: `Inserted ${data.insertedCount ?? data.rowCount ?? 0} rows into ${data.tableName}.`,
      });
    } catch (e) {
      console.error(e);
      Swal.fire({ icon: "error", title: "Error", text: e.message || "Failed to run fairshare" });
    } finally {
      setRunningFairshare(false);
    }
  };

  if (loadingScenathons) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-4 text-sm text-slate-600 shadow-sm">
          Loading scenathons...
        </div>
      </div>
    );
  }

  const hasPendingTasks = tasksInProgress.length > 0;

  return (
    <>
      <div className="mt-10 w-full rounded-xl bg-slate-50 shadow-sm">
        <main className="mx-auto w-full px-4 py-8 sm:px-6 lg:px-4">

          {/* Pending banner */}
          {hasPendingTasks && (
            <div className="mb-4 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
              <span className="font-medium">{tasksInProgress.length} trade adjustment task{tasksInProgress.length !== 1 ? "s" : ""} processing in background.</span>
              {" "}You can keep working while they finish.
              <div className="mt-3 flex w-full max-w-sm items-center gap-1" aria-hidden="true">
                <span className="h-1 flex-1 animate-pulse rounded-full bg-blue-300" />
                <span className="h-1 flex-1 animate-pulse rounded-full bg-blue-400" style={{ animationDelay: "0.15s" }} />
                <span className="h-1 flex-1 animate-pulse rounded-full bg-blue-500" style={{ animationDelay: "0.3s" }} />
              </div>
            </div>
          )}

          {/* Header + selects */}
          <div className="rounded-xl bg-white p-6 ring-1 ring-[#E3E5E6]">
            <h1 className="text-2xl font-semibold text-slate-900">Trade Adjustment</h1>
            {/* <p className="mt-1 text-sm text-slate-600">
              View one record per country submitted from Scenathon.
            </p> */}

            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {!selectedScenathon?.id && (
                <Select
                  label="Scenathon"
                  value={scenathonId}
                  onChange={setScenathonId}
                  options={scenathonOptions}
                  placeholder="Select scenathon"
                  className="w-full"
                />
              )}

              <Select
                label="Pathway"
                value={pathwayId}
                onChange={setPathwayId}
                options={pathways}
                placeholder={
                  scenathonId
                    ? loadingPathways ? "Loading pathways..." : "Select pathway"
                    : "Select scenathon first"
                }
                disabled={!scenathonId || loadingPathways}
                className="w-full"
              />

            </div>
          </div>

          {/* Table section */}
          <div className="mt-6 rounded-xl bg-white ring-1 ring-[#E3E5E6]">
            {!scenathonId || !pathwayId ? (
              <div className="p-10 text-center text-sm text-slate-500">
                Select a scenathon and a pathway to view trade data.
              </div>
            ) : loading ? (
              <div className="p-10 text-center text-sm text-slate-500">Loading trade data...</div>
            ) : error ? (
              <div className="p-6 text-sm text-rose-700 bg-rose-50 rounded-xl">{error}</div>
            ) : rows.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-500">
                No trade data found for this selection.
              </div>
            ) : (
              <div className="p-4">
                {/* Row count + Send All button */}
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="text-sm text-slate-500">
                    <span className="font-medium text-slate-800">{filtered.length}</span> rows
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={handleRefreshTable}
                      disabled={!scenathonId || !pathwayId || loading}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                      </svg>
                      {loading ? (
                        <>
                          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-400 border-t-transparent"></span>
                          Refreshing...
                        </>
                      ) : (
                        "Reload table"
                      )}
                    </button>

                    <button
                      onClick={handleSubmitAllTasks}
                      disabled={filtered.length === 0 || enqueuing}
                      className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {enqueuing ? (
                        <>
                          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                          Executing...
                        </>
                      ) : (
                        "Execute Trade Adjustment"
                      )}
                    </button>
                  </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        {COLUMNS.map((col) => (
                          <th
                            key={col.key}
                            onClick={() => handleSort(col.key)}
                            className="cursor-pointer select-none whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-800"
                          >
                            {col.label}
                            <SortIcon direction={sortKey === col.key ? sortDir : null} />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((row, i) => (
                        <tr
                          key={i}
                          className={`border-b border-slate-100 ${
                            i % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                          } hover:bg-blue-50/40 transition-colors`}
                        >
                          <td className="whitespace-nowrap px-4 py-2 text-slate-700">
                            {row.id ?? "—"}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-slate-700">
                            {row.scenathon_id ?? "—"}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 font-medium text-slate-800">
                            {row.country || "—"}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-slate-700">
                            {fmtDate(row.date)}
                          </td>

                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Progress Panel */}
      {tasksInProgress.length > 0 && (
        <div className="fixed bottom-0 right-0 m-4 max-w-sm rounded-lg border border-blue-200 bg-blue-50 p-4 shadow-lg z-40">
          <h3 className="mb-3 font-semibold text-blue-900">Tasks in Progress</h3>
          <div className="space-y-2">
            {tasksInProgress.map((taskId) => (
              <div
                key={taskId}
                className="flex items-center justify-between rounded bg-white p-2 text-sm"
              >
                <div className="flex items-center gap-2">
                  <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-blue-400 border-t-blue-600"></span>
                  <span className="text-slate-700">Task #{taskId}</span>
                </div>
                <span className="text-xs font-medium text-blue-600">
                  {taskStatuses[taskId] === "done" ? "✓ Done" : "Processing..."}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
